<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import ClassScheduleDialog from '@/components/utilities/ClassScheduleDialog.vue';
import UiTermSelect from '@/components/ui/UiTermSelect.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useTerm } from '@/composables/useTerm';
import { useToast } from '@/composables/useToast';
import { WEEKDAYS, formatTimeRange } from '@/utils/format';
import { gradesForLevel } from '@/constants/accounts';

// Weekly schedule of a section: pick a grade (tab), a section and a term,
// then add, edit or remove class periods. Each term has its own schedule; a
// term with no classes yet can start as a copy of the term before it. High school by default; the elementary page
// passes its own grades.
const props = defineProps({
  grades: { type: Array, default: null },
});

useSessionGuard();

// The Junior/Senior High admins see their own grades; the school-wide
// admin sees all of them.
const store = useStore();
const gradeList = props.grades ?? gradesForLevel(store.getters.activeRoleLevel);
const tabs = gradeList.map((g) => ({ id: g, name: g }));

const year = computed(() => store.getters.getFilterSelected);
// ?grade=&section= (from the Subjects page's "wrong grade" links) opens on
// that section; only the first load uses it.
const route = useRoute();
const grade = shallowRef(gradeList.includes(route.query.grade) ? route.query.grade : gradeList[0]);
let wantedSection = route.query.section ?? null;
const sections = shallowRef([]);
const sectionId = shallowRef(null);
const section = computed(() => sections.value.find((s) => s.id === sectionId.value) ?? null);

async function loadSections() {
  sections.value = [];
  sectionId.value = null;
  try {
    const rows = await apiRequest('get', `/rooms-section/${encodeURIComponent(grade.value)}`);
    sections.value = Array.isArray(rows) ? rows : [];
    const wanted = sections.value.find((s) => s.room_section === wantedSection);
    wantedSection = null;
    sectionId.value = (wanted ?? sections.value[0])?.id ?? null;
  } catch {
    sections.value = [];
  }
}
watch(grade, loadSections, { immediate: true });

const { hasTerms, terms, term, termQuery } = useTerm();
const { items, loading, error, load } = useListResource(async () => {
  if (!sectionId.value || !year.value) return [];
  const rows = await apiRequest(
    'get',
    `/enroll-student/getClassProgramm/${encodeURIComponent(grade.value)}/${sectionId.value}/${year.value}?${termQuery.value}`,
  );
  return (rows || []).filter((r) => r.roomId === sectionId.value);
});
watch([sectionId, year, term], load);

// "Copy <previous term>": offered when this section has no classes in the
// chosen term yet (the server refuses otherwise, so nothing is doubled).
const previousTerm = computed(() => terms.value[terms.value.indexOf(term.value) - 1] ?? null);
const canCopy = computed(
  () => !!section.value && !!previousTerm.value && !loading.value && !error.value && !items.value.length,
);

const headers = [
  { title: 'Time', key: 'time', sortable: false },
  { title: 'Teacher', key: 'name' },
  { title: 'Subject', key: 'subject_title' },
];
const groupBy = [{ key: 'day', order: 'asc' }];
const dayIndex = (d) => (WEEKDAYS.includes(d) ? WEEKDAYS.indexOf(d) : 99);
const customKeySort = { day: (a, b) => dayIndex(a) - dayIndex(b) };

const editing = shallowRef(null);
const formOpen = shallowRef(false);
function openForm(period = null) {
  editing.value = period;
  formOpen.value = true;
}

const { confirm } = useConfirm();
const { run } = useAsyncAction();
const toast = useToast();

const copying = shallowRef(false);
async function copyPreviousTerm() {
  const from = previousTerm.value;
  const ok = await confirm({
    title: `Copy the ${from} schedule?`,
    description: `${section.value.room_section}'s class periods from the ${from} will be added to the ${term.value}. You can change them afterwards.`,
    confirmText: 'Copy schedule',
  });
  if (!ok) return;
  copying.value = true;
  try {
    const body = await apiRequest('post', '/enroll-student/copySectionSchedule', {
      roomId: sectionId.value,
      schoolYearId: Number(year.value),
      fromTerm: from,
      toTerm: term.value,
    });
    assertOk(body);
    toast.success(body?.msg || 'Schedule copied.');
    load();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not copy the schedule.'));
  } finally {
    copying.value = false;
  }
}

async function remove(period) {
  const ok = await confirm({
    title: 'Remove class period?',
    description: `${period.subject_title} on ${period.day} will be removed from the schedule.`,
    confirmText: 'Remove',
    destructive: true,
  });
  if (!ok) return;
  if (await run(() => apiRequest('delete', `/enroll-student/deleteAvailabilitySchedule/${period.availId}`), { success: 'Class period removed.', error: 'Could not remove the class period.' })) {
    load();
  }
}

// `status` on a row is the joined subject's, not the period's, so it can't
// gate Edit; hiding on it left some rows with an empty ⋯ menu.
const rowActions = (period) => [
  { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(period) },
  { title: 'Remove', icon: 'mdi-trash-can-outline', destructive: true, onClick: () => remove(period) },
];
</script>

<template>
  <UiTableCard
    v-model:tab="grade"
    title="Classroom schedules"
    description="The weekly class periods of each section."
    :tabs="tabs"
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    :searchable="false"
    :items-per-page="-1"
    :group-by="groupBy"
    groups-open
    :custom-key-sort="customKeySort"
    :empty-text="section ? `No class periods for this section${term ? ` in the ${term}` : ''} yet.` : 'Choose a section.'"
    item-value="availId"
    row-label="subject_title"
    :row-actions="rowActions"
  >
    <template v-if="hasTerms" #toolbar>
      <UiTermSelect v-model="term" :items="terms" density="compact" />
    </template>
    <template #actions>
      <v-select
        v-model="sectionId"
        :items="sections"
        item-title="room_section"
        item-value="id"
        placeholder="Section"
        prepend-inner-icon="mdi-google-classroom"
        density="compact"
        hide-details class="card-search"
        :no-data-text="`No sections in ${grade}`"
      />
      <v-btn v-if="canCopy" variant="outlined" prepend-icon="mdi-content-copy" :loading="copying" @click="copyPreviousTerm">
        Copy {{ previousTerm }}
      </v-btn>
      <v-btn color="primary" prepend-icon="mdi-plus" :disabled="!section" @click="openForm()">
        Add period
      </v-btn>
    </template>
    <template #[`item.time`]="{ item }">
      <span class="text-no-wrap">{{ formatTimeRange(item.times_slot_from, item.times_slot_to) }}</span>
    </template>
  </UiTableCard>

  <ClassScheduleDialog
    v-model="formOpen"
    :item="editing"
    :grade="grade"
    :section="section"
    :periods="items"
    :term="term"
    @saved="load"
  />
</template>
